import { hexToRGB } from '@repo-level/core-front';
import styled, { css } from 'styled-components/macro';

import { device } from 'components/styles/MediaQuery';
import { ScCheckboxBlock } from 'components/form/primitives/styled';

const TITLE_LENGTH = 40;
const OFFER_LENGTH_SMALL = 36;
const OFFER_LENGTH_BIG = 60;

interface ScBonusCardProps {
  isGroup: boolean;
  banner?: string;
  titleLength: number;
  offerLength: number;
}

export const ScBonusCard = styled.div<ScBonusCardProps>`
  position: relative;
  display: flex;
  flex-direction: column;

  & > a {
    z-index: 2;
  }

  .banner {
    cursor: pointer;
    background: ${({ banner }) => `url(${banner}) top left no-repeat`};
    background-size: cover;
    min-height: 256px;
    border-radius: 16px;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr minmax(0, 1fr);

    & > .content {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 24px;
      font-weight: 800;
      font-size: 20px;
      line-height: 28px;
      letter-spacing: 0.2px;
      padding: 24px 16px 24px 48px;
      color: ${({ theme }) => theme.whiteColor};

      & > header {
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-width: 420px;

        & > .title {
          color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
          white-space: normal;
          word-wrap: break-word;
          overflow: hidden;
          ${({ titleLength }) =>
            titleLength <= TITLE_LENGTH &&
            css`
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              text-overflow: ellipsis;
              overflow: hidden;
            `}
        }

        & > .offer {
          font-size: 28px;
          line-height: 36px;

          ${({ offerLength }) =>
            offerLength <= OFFER_LENGTH_BIG &&
            css`
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              text-overflow: ellipsis;
              overflow: hidden;
            `}

          @media (${device.mobile}) {
            font-size: ${({ offerLength }) =>
              offerLength <= OFFER_LENGTH_SMALL ? '28px' : '24px'};
            line-height: ${({ offerLength }) =>
              offerLength <= OFFER_LENGTH_SMALL ? '36px' : '32px'};

            ${({ offerLength }) =>
              offerLength >= OFFER_LENGTH_BIG &&
              css`
                -webkit-line-clamp: 3;
              `}
          }
        }
      }

      & .btn-content {
        white-space: normal;
      }

      & > button,
      & > a {
        min-width: 144px;
        max-width: 232px;
        margin-top: auto;
      }
    }

    & > .image-container {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: flex-end;
      overflow: hidden;

      & .image {
        object-fit: contain;
        transition: transform 0.3s;
      }
    }

    @media (hover: hover) {
      &:hover .image-container > img {
        transform: scale(1.1);
      }
    }

    @media (${device.mobileS}) {
      grid-template-columns: 1fr;
      min-height: ${({ isGroup }) => (isGroup ? '312px' : '272px')};
      position: relative;

      & > .content {
        padding: 24px;
        overflow: hidden;
        position: relative;
        gap: 24px;
        z-index: 2;

        & > header {
          gap: 12px;
        }

        & > button {
          max-width: 64%;
          min-width: 64%;
        }
      }

      & > .image-container {
        position: absolute;
        bottom: 0;
        right: 0;
        width: auto;
        height: auto;
        border-bottom-right-radius: 16px;

        & .image {
          max-width: 168px;
          max-height: 140px;
        }
      }
    }
  }
`;

type ScBonusCardSmallProps = Pick<ScBonusCardProps, 'banner'> & {
  withTimer?: boolean;
  isSelected?: boolean;
  minDepositError?: boolean;
};

export const ScBonusCardSmall = styled.div<ScBonusCardSmallProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 368px;
  @media (${device.mobile}) {
    width: 100%;
  }

  .banner {
    background: ${({ banner }) => `url(${banner}) top left no-repeat`};
    cursor: pointer;
    background-size: cover;
    position: relative;
    height: 100%;
    border-radius: 12px;
    width: 100%;
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr) 56px;
    grid-template-rows: 1fr;
    align-items: center;
    padding: 8px;

    ${ScCheckboxBlock} {
      margin: 0;
      justify-content: center;
    }

    ${({ isSelected }) =>
      isSelected &&
      css`
        &::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 12px;
          padding: 1px;
          background: ${({ theme }) => theme.greenMarineGradient};

          -webkit-mask: linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
        }
      `}

    ${({ minDepositError }) =>
      minDepositError &&
      css`
        opacity: 0.4;
        &::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 12px;
          padding: 2px;
          background: ${({ theme }) => theme.neonPinkGradient};

          -webkit-mask: linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
        }
      `}

    & > .content {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      padding: 0 12px;

      @media (${device.mobileS}) {
        padding: 0 8px;
      }

      & > .timer-label {
        display: flex;
        align-self: flex-start;
        align-items: center;
        column-gap: 4px;
        padding: 6px 8px;
        border-radius: 8px;
        margin-bottom: 4px;
        background-color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.2)};
        color: ${({ theme }) => theme.whiteColor};

        & > svg path {
          fill: ${({ theme }) => theme.whiteColor};
        }

        @media (max-width: 380px) {
          .timer {
            font-size: 10px;
          }
        }
      }

      & > .title {
        font-size: 12px;
        line-height: 16px;
        letter-spacing: 0px;
        font-weight: 800;
        color: ${({ theme }) => theme.darkWhiteColor};
        overflow: hidden;
        text-overflow: ellipsis;
        height: 100%;
        max-height: 16px;
        white-space: nowrap;
      }

      & > .offer {
        height: 100%;
        align-items: start;
        text-align: left;
        color: ${({ theme }) => theme.whiteColor};
        font-weight: 700;
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0.16px;
        max-height: 48px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        @media (${device.mobileS}) {
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0;
          max-height: 40px;
        }
      }
    }

    & > .image-container {
      width: 72px;
      height: 60px;
      display: flex;
      align-items: center;
      @media (${device.mobileS}) {
        padding: 8px 0;
      }

      & .image {
        width: 100%;
        height: 80px;
        object-fit: contain;
      }
    }
  }

  .min-dep {
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0px;
    margin-top: 2px;
    padding-left: 16px;
    color: ${({ theme }) => theme.neonPink};
  }
`;

export const ScBonusesGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;

  @media (${device.mobileS}) {
    display: block;
  }
`;

interface ScBonusProps {
  isUsed: boolean;
  isActive: boolean;
}

export const ScBonus = styled.div<ScBonusProps>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  padding: 1px 4px;
  min-width: 144px;
  min-height: 44px;
  background: ${({ theme }) => hexToRGB(theme.whiteColor, 0.2)};
  border: 3px solid transparent;
  border-radius: 12px;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;

  ${({ isActive, isUsed }) =>
    isUsed
      ? css`
          opacity: 0.2;
        `
      : isActive &&
        css`
          border: ${({ theme }) =>
            `3px solid  ${hexToRGB(theme.blueColor, 0.6)}`};
          box-shadow: ${({ theme }) =>
            `0px 0px 4px  ${hexToRGB(theme.blueColor, 0.6)}`};

          &::after {
            content: '';
            position: absolute;
            border: ${({ theme }) => `1px solid  ${theme.whiteColor}`};
            border-radius: 11px;
            top: -2px;
            left: -2px;
            width: calc(100% + 2px);
            height: calc(100% + 2px);
          }
        `}

  & > svg {
    width: 24px;
    height: 36px;
    object-fit: contain;
    flex-shrink: 0;
  }

  & > span {
    text-overflow: ellipsis;
    overflow: hidden;
  }

  @media (max-width: 370px) {
    min-width: 100%;
  }
`;

export const ScActiveBonusDetails = styled.div<{ withPaddingBottom: boolean }>`
  padding: 0 24px;
  width: 100%;
  margin-top: -4px;

  .inner-container {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background-color: ${({ theme }) => theme.grayColor5};
    padding: 28px 24px 0;
    padding-bottom: ${({ withPaddingBottom }) => withPaddingBottom && '24px'};
    border-radius: 0px 0px 16px 16px;
    overflow: hidden;
    font-size: 14px;
    line-height: 20px;

    & .description a {
      text-decoration: underline;
      background-color: transparent;
      cursor: pointer;
      color: ${({ theme }) => theme.greenColor};
      transition: opacity 0.4s;
      @media (hover: hover) {
        &:hover {
          opacity: 0.6;
        }
      }
    }

    ol,
    ul {
      padding-left: 22px;
    }

    & > .details {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;

      .details__item {
        height: 100%;
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        background-color: ${({ theme }) => theme.grayColor6};
        padding: 6px 8px;
        border-radius: 8px;

        & > svg {
          flex-shrink: 0;
          path {
            fill: ${({ theme }) => theme.whiteColor};
          }
        }
      }
    }
  }

  @media (${device.mobileS}) {
    padding: 0 8px;

    .inner-container {
      padding: 20px 16px 0;
      padding-bottom: ${({ withPaddingBottom }) => withPaddingBottom && '16px'};

      & > .details {
        display: flex;
        flex-direction: column;
      }

      .details__item {
        width: 100%;
      }
    }
  }
`;

interface ScTermsAndConditionsProps {
  isOpen: boolean;
}

export const ScTermsAndConditions = styled.div<ScTermsAndConditionsProps>`
  position: relative;

  & > div {
    padding-bottom: 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    ol,
    ul {
      flex-direction: column;
      display: flex;
      gap: 8px;
    }
  }

  ${({ isOpen }) =>
    !isOpen &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          180deg,
          rgba(31, 32, 44, 0.7) 0%,
          #1f202c 100%
        );
      }
    `}

  @media (${device.mobileS}) {
    & > div {
      height: ${({ isOpen }) => !isOpen && 'calc(100% - 1px)'};
      padding-bottom: 48px;
      overflow: hidden;
    }
  }
`;

export const ScTermsAndConditionsBlock = styled.div`
  padding: 0 8px;
  color: ${({ theme }) => theme.whiteColor};

  & > div {
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.grayColor4};
    border-radius: 0px 0px 12px 12px;
    padding: 20px 16px 16px 16px;
    gap: 8px;

    ol,
    ul {
      flex-direction: column;
      display: flex;
      gap: 8px;
    }
  }
`;

export interface ScShowMoreBtnProps {
  isOpen: boolean;
  isListItem?: boolean;
}

export const ScMoreBtn = styled.div<ScShowMoreBtnProps>`
  cursor: pointer;
  color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
  display: flex;
  align-items: center;
  margin-top: 4px;
  z-index: 1;

  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;

    svg {
      width: 16px;
      height: 16px;
      object-fit: contain;
      transform: ${({ isOpen }) =>
        isOpen ? 'rotate(-90deg)' : 'rotate(90deg)'};
      transition: transform 0.3s;

      path {
        fill: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
      }
    }
  }

  @media (hover: hover) {
    &:hover {
      color: ${({ theme }) => theme.whiteColor};

      svg {
        path {
          fill: ${({ theme }) => theme.whiteColor};
        }
      }
    }
  }

  ${({ isListItem }) =>
    isListItem &&
    css`
      margin-left: 16px;
      justify-content: center;

      @media (${device.mobileS}) {
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        z-index: 3;
        height: 72px;
        padding-bottom: 16px;
        margin-left: 0;
        align-items: flex-end;
      }
    `}
`;
